import {
  ChangeEvent,
  FormEvent,
  InputHTMLAttributes,
  ReactNode,
  useRef,
  useState,
} from 'react';
import { twMerge } from 'tailwind-merge';
import Field from './Field';
import { InputPrimitive } from './Input';

type CheckboxPrimitiveProps = Omit<
  InputHTMLAttributes<HTMLInputElement>,
  'type' | 'id'
> & {
  id: string;
  validationMessages?: Partial<Record<ValidationConstraint, string>>;
};

type ValidationConstraint = Exclude<keyof ValidityState, 'valid'>;

export default function Checkbox({
  id,
  label,
  className,
  description,
  ...checkboxProps
}: {
  label: string;
  description?: ReactNode;
  validationMessages?: Partial<Record<ValidationConstraint, string>>;
} & CheckboxPrimitiveProps) {
  const ref = useRef<HTMLInputElement>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const handleInvalid = (event: FormEvent<HTMLInputElement>) => {
    const input = event.currentTarget;
    setErrorMessage(input.validationMessage);
  };

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    const input = event.currentTarget;
    if (input.validity.valid) {
      setErrorMessage(null);
    }

    checkboxProps.onChange?.(event);
  };

  return (
    <Field.Root className={twMerge(className, 'relative')}>
      <div className="flex items-start gap-3">
        <InputPrimitive
          // eslint-disable-next-line react/jsx-props-no-spreading -- the props extend the checkbox primitive props
          {...checkboxProps}
          type="checkbox"
          id={id}
          ref={ref}
          aria-describedby={`${id}-help-text ${id}-error-text`}
          onInvalid={handleInvalid}
          onChange={handleChange}
        />
        <div className="flex flex-col gap-1">
          <Field.Label
            className="cursor-pointer text-sm font-medium text-gray-700"
            htmlFor={id}
          >
            {`${label}${checkboxProps.required ? '' : ' (optional)'}`}
          </Field.Label>
          {errorMessage && (
            <Field.ErrorText id={`${id}-error-text`}>
              {errorMessage}
            </Field.ErrorText>
          )}
          {description && !errorMessage && (
            <Field.HelpText id={`${id}-help-text`}>
              {description}
            </Field.HelpText>
          )}
        </div>
      </div>
    </Field.Root>
  );
}
